import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUserProfile } from '../../contexts/user/hooks';

const isAuthenticated = () => {
  return !!localStorage.getItem('authToken');
};

// Routes accessible without active subscription
const SUBSCRIPTION_EXEMPT_PATHS = ['/app/billing', '/app/profile', '/app/support'];

// Routes accessible without verified email
const VERIFICATION_EXEMPT_PATHS = ['/app/profile', '/app/billing', '/app/support'];

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  const isExempt = SUBSCRIPTION_EXEMPT_PATHS.some(path => location.pathname.startsWith(path));
  const isVerificationExempt = VERIFICATION_EXEMPT_PATHS.some(path => location.pathname.startsWith(path));

  // Email verification gate first (non-exempt routes only)
  if (!isVerificationExempt) {
    return (
      <EmailVerificationGate>
        {isExempt ? children : <SubscriptionGate>{children}</SubscriptionGate>}
      </EmailVerificationGate>
    );
  }

  // Exempt paths skip both gates
  return <>{children}</>;
};

const EmailVerificationGate = ({ children }: { children: React.ReactNode }) => {
  const { profile, isLoading } = useUserProfile();

  // Wait for profile to load
  if (isLoading || !profile) {
    return <>{children}</>;
  }

  // Owner and admin bypass verification check
  if (profile.role === 'owner' || profile.role === 'admin') {
    return <>{children}</>;
  }

  // Email must be verified
  if (profile.emailVerified === false) {
    return <Navigate to="/app/profile" replace />;
  }

  return <>{children}</>;
};

const SubscriptionGate = ({ children }: { children: React.ReactNode }) => {
  const { profile, isLoading } = useUserProfile();

  // Wait for profile to load
  if (isLoading || !profile) {
    return <>{children}</>;
  }

  // Owner and admin bypass subscription check
  if (profile.role === 'owner' || profile.role === 'admin') {
    return <>{children}</>;
  }

  // Active subscription required
  if (profile.subscriptionStatus !== 'active') {
    return <Navigate to="/app/billing" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
